export type DiscoverableKind = 'attraction' | 'settlement' | 'history' | 'hotspot'

export interface DiscoveryRecord {
  kind: DiscoverableKind
  seenAt: string
  photographedAt?: string
}

export interface QuestState {
  version: 1
  discoveries: Record<string, DiscoveryRecord>
  announced?: string[]
  /** Quest ids Don Rafa (or another giver) has handed over. */
  assigned?: string[]
}

export interface LegacyThumb {
  id: string
  kind: DiscoverableKind
  dataUrl: string
}

const STORAGE_KEY = 'roam:arenal:quest:v1'

/** Survives localStorage failures so the session can keep counting. */
let memory: QuestState | null = null

function emptyState(): QuestState {
  return { version: 1, discoveries: {}, announced: [], assigned: [] }
}

function normalize(parsed: Partial<QuestState>): QuestState {
  if (parsed.version !== 1 || !parsed.discoveries || typeof parsed.discoveries !== 'object') {
    return emptyState()
  }
  return {
    version: 1,
    discoveries: parsed.discoveries,
    announced: Array.isArray(parsed.announced) ? parsed.announced : [],
    assigned: Array.isArray(parsed.assigned) ? parsed.assigned : [],
  }
}

export function loadQuestState(): QuestState {
  if (memory) return memory
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    memory = raw ? normalize(JSON.parse(raw) as Partial<QuestState>) : emptyState()
    return memory
  } catch {
    memory = emptyState()
    return memory
  }
}

export function saveQuestState(state: QuestState): void {
  memory = state
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Degrade to in-memory.
  }
}

/** Returns true only the first time an id is recorded. */
export function markSeen(state: QuestState, id: string, kind: DiscoverableKind): boolean {
  if (state.discoveries[id]) return false
  state.discoveries[id] = { kind, seenAt: new Date().toISOString() }
  return true
}

export function markPhotographed(state: QuestState, id: string, kind: DiscoverableKind): void {
  const record = state.discoveries[id]
  const now = new Date().toISOString()
  if (!record) {
    state.discoveries[id] = { kind, seenAt: now, photographedAt: now }
    return
  }
  record.photographedAt = now
}

/** Clears the photo flag; the discovery itself remains. */
export function markUnphotographed(state: QuestState, id: string): void {
  const record = state.discoveries[id]
  if (!record) return
  delete record.photographedAt
}

/** Field name from the localStorage-only build, read so those saves can be migrated. */
const LEGACY_THUMB_FIELD = 'photoThumb'

/**
 * Strips pre-IndexedDB thumbnails out of the ledger so they can be re-homed.
 */
export function takeLegacyThumbs(state: QuestState): LegacyThumb[] {
  const legacy: LegacyThumb[] = []
  for (const [id, record] of Object.entries(state.discoveries)) {
    const raw = record as DiscoveryRecord & Record<string, unknown>
    const dataUrl = raw[LEGACY_THUMB_FIELD]
    if (typeof dataUrl !== 'string') continue
    legacy.push({ id, kind: record.kind, dataUrl })
    delete raw[LEGACY_THUMB_FIELD]
  }
  return legacy
}

export function discoveredCount(state: QuestState): number {
  return Object.keys(state.discoveries).length
}

export function photographedCount(state: QuestState): number {
  return Object.values(state.discoveries).filter((record) => record.photographedAt).length
}

export function isDiscovered(state: QuestState, id: string): boolean {
  return Boolean(state.discoveries[id])
}

export function getRecord(state: QuestState, id: string): DiscoveryRecord | undefined {
  return state.discoveries[id]
}

export function markAnnounced(state: QuestState, questId: string): void {
  if (!state.announced) state.announced = []
  if (state.announced.includes(questId)) return
  state.announced.push(questId)
}

export function hasAnnounced(state: QuestState, questId: string): boolean {
  return Boolean(state.announced?.includes(questId))
}

/** Returns true only the first time a quest is assigned by a giver. */
export function markAssigned(state: QuestState, questId: string): boolean {
  if (!state.assigned) state.assigned = []
  if (state.assigned.includes(questId)) return false
  state.assigned.push(questId)
  return true
}

export function isAssigned(state: QuestState, questId: string): boolean {
  return Boolean(state.assigned?.includes(questId))
}
